import { Component, OnInit, OnDestroy, Injectable, AfterViewInit } from '@angular/core';
import { Subject, fromEvent, merge, of, Observable } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { CommonModule } from '@angular/common';

import { Storage } from '@ionic/storage';
import { Router } from '@angular/router';
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SyncWarningDialog } from './components/warnings/unsynced-warning';

// Service to track network status
@Injectable({
  providedIn: 'root'
})
export class NetworkStatusService {
  private destroy$ = new Subject<void>();

  isOnline$ = merge(
    of(navigator.onLine),
    fromEvent(window, 'online').pipe(map(() => true)),
    fromEvent(window, 'offline').pipe(map(() => false))
  ).pipe(
    startWith(navigator.onLine)
  );

  constructor() { console.log('khwekfhweih') }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

// Main offline indicator component
@Component({
  selector: 'app-offline-indicator',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatChipsModule],
  template: `
    <div class="offline-indicator-container" *ngIf="!isLoginPage">
      <div 
        class="connection-status offline" 
        *ngIf="!(isOnline$ | async)"
      >
        <mat-icon color="warn">wifi_off</mat-icon>
      </div>
    </div>
  `,
  styles: [`
    .mat-icon{
      width: 40px !important;
    }
    .offline-indicator-container {
      position: fixed;
      z-index: 1000;
      display: flex;
      flex-direction: column;
      gap: 12px;
      max-width: 350px;
    }

    .offline-chip-set {
      display: flex;
      justify-content: flex-end;
    }

    .offline-chip {
      background-color: #f44336 !important;
      color: white !important;
      font-weight: 500;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0% { opacity: 1; }
      50% { opacity: 0.7; }
      100% { opacity: 1; }
    }

    .connection-status {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 16px;
      border-radius: 24px;
      font-size: 14px;
      font-weight: 500;
      transition: all 0.3s ease;
      backdrop-filter: blur(10px);
      border: 1px solid transparent;
    }

    .connection-status.online {
      background-color: rgba(76, 175, 80, 0.1);
      color: #4caf50;
      border-color: rgba(76, 175, 80, 0.3);
    }

    .connection-status.offline {
      background-color: rgba(244, 67, 54, 0.1);
      color: #f44336;
      border-color: rgba(244, 67, 54, 0.3);
    }

    .cancelled-requests {
      background-color: rgba(255, 152, 0, 0.1);
      border: 1px solid rgba(255, 152, 0, 0.3);
      border-radius: 8px;
      padding: 12px;
      display: flex;
      flex-direction: column;
      gap: 8px;
      backdrop-filter: blur(10px);
    }

    .warning-icon {
      color: #ff9800;
    }

    .cancelled-text {
      font-size: 13px;
      color: #ff9800;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .retry-button {
      align-self: flex-start;
      font-size: 12px;
      height: 32px;
      min-height: 32px;
    }

    .status-text {
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    /* Mobile responsiveness */
    @media (max-width: 768px) {
      .offline-indicator-container {
        bottom: 6px;
        left: 10px;
        right: 10px;
        width: 60px;
      }
      
      .cancelled-requests {
        font-size: 12px;
      }
    }
  `]
})
export class OfflineIndicatorComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();
  isOnline$!: Observable<boolean>;
  cancelledRequests: any[] = [];
  property = localStorage.getItem('currentProperty');
  isLoginPage = false;
  
  dialog = inject(MatDialog);
  private dialogOpened = false;

  constructor(
    private storage: Storage,
    private networkStatusService: NetworkStatusService,
    private snackBar: MatSnackBar,
    private offlineIndicatorService: OfflineIndicatorService,
    private router: Router
  ) {
    // Check if current route includes 'login'
    this.isLoginPage = this.router.url.includes('login');
    
    // Initialize isOnline$ only if not on login page
    if (!this.isLoginPage) {
      this.isOnline$ = this.networkStatusService.isOnline$;
    }

    // Listen to route changes
    this.router.events.subscribe(() => {
      this.isLoginPage = this.router.url.includes('login');
      
      // Update isOnline$ based on route
      if (!this.isLoginPage && !this.isOnline$) {
        this.isOnline$ = this.networkStatusService.isOnline$;
      }
    });
  }

  async ngAfterViewInit(): Promise<void> {
    // Empty for now
  }

  async ngOnInit() {
    // Skip all initialization if on login page
    if (this.isLoginPage) {
      return;
    }

    this.isOnline$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isOnline => {
      if (isOnline) {
        this.showOnlineNotification();
        // Clear cancelled requests when back online
        setTimeout(() => {
          this.cancelledRequests = [];
        }, 3000);
      } else {
        this.showOfflineNotification();
      }
    });

    // Listen for cancelled requests from the interceptor
    this.offlineIndicatorService.cancelledRequests$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(cancelledRequest => {
      this.addCancelledRequest(cancelledRequest);
    });

    const newMeters = await this.storage.get('unsyncedMeters');
    const unsynced = (newMeters || []).filter((m: any) => m.captured === 'NOTSYNCED');
    console.log(unsynced)
    console.log(this.dialogOpened)
    if (unsynced.length > 0 && !this.dialogOpened) {
      this.dialogOpened = true; // prevent duplicates
      const dialogRef = this.dialog.open(SyncWarningDialog, {
        data: {
          from: '',
          count: unsynced.length,
          meters: unsynced,
          message: 'Please sync when back online .'
        }
      });

      dialogRef.afterClosed().subscribe(async (result: any) => {
        this.dialogOpened = false; // allow reopen later if needed
        if (result === 'sync') {
          this.dialog.closeAll();
          setTimeout(() => this.router.navigateByUrl('main/sync'), 0);
        } else {
          this.dialog.closeAll();
        }
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  addCancelledRequest(request: any) {
    this.cancelledRequests.push(request);
  }

  retryRequests() {
    // This would typically trigger a retry mechanism in your service
    this.snackBar.open('Requests will retry when connection is restored', 'OK', {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  private showOfflineNotification() {
    this.snackBar.open('You are now offline.', 'OK', {
      duration: 2000,
      panelClass: ['offline-snackbar'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  private showOnlineNotification() {
    // this.snackBar.open('Connection restored! You are back online.', 'OK', {
    //   duration: 3000,
    //   panelClass: ['online-snackbar'],
    //   horizontalPosition: 'center',
    //   verticalPosition: 'bottom'
    // });
  }
}

// Service to manage offline indicator state
@Injectable({
  providedIn: 'root'
})
export class OfflineIndicatorService {
  private cancelledRequestsSubject = new Subject<any>();
  public cancelledRequests$ = this.cancelledRequestsSubject.asObservable();

  addCancelledRequest(request: any) {
    this.cancelledRequestsSubject.next({
      url: request.url,
      timestamp: new Date(),
      id: Date.now() + Math.random()
    });
  }
}

// HTTP Interceptor function to integrate with the indicator
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { throwError } from 'rxjs';

export const offlineInterceptor: HttpInterceptorFn = (request, next) => {
  const offlineIndicatorService = inject(OfflineIndicatorService);
  const router = inject(Router);

  // Skip offline check if on login page
  if (router.url.includes('login')) {
    return next(request);
  }

  // Check if user is offline
  if (!navigator.onLine) {
    console.warn('User is offline. Cancelling request:', request.url);

    // Add to cancelled requests list
    offlineIndicatorService.addCancelledRequest(request);

    // Return error response
    return throwError(() => new HttpErrorResponse({
      status: 0,
      statusText: '',
      url: request.url,
      error: { message: 'No internet connection' }
    }));
  }

  return next(request);
};